import { useContext, useEffect, useState } from 'react';
import { Card, Col, Divider, Row, Spin } from 'antd';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { IDashboardInvoicesByTypeItem } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { poeValorComPontosEmMil } from '../../../../utils/Conversores';

interface InvoicesByTypeChartProps {
    data: IDashboardInvoicesByTypeItem[];
    isLoading: boolean;
    compact?: boolean;
}

const TYPE_COLORS: Record<string, string> = {
    NFC: "#fa8c16",
    NFS: "#1677ff",
    NFE: "#52c41a",
};

const DEFAULT_COLOR = '#fa8c16';

export default function InvoicesByTypeChart({ data, isLoading, compact }: InvoicesByTypeChartProps) {
    const { theme } = useContext(ThemeContext);
    const total = data.reduce((acc, item) => acc + item.total, 0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth < 1200);

    useEffect(() => {
        const handler = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth < 1200);
        };

        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    const pieSize = isMobile ? 180 : isTablet ? 220 : 250;
    const innerRadius = isMobile ? 45 : isTablet ? 60 : 70;
    const outerRadius = isMobile ? 70 : isTablet ? 90 : 105;

    if (compact) {
        return (
            <Card
                title={
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Notas por tipo</div>
                        <div style={{ fontWeight: 400, fontSize: 13, color: '#888' }}>
                            Quantidade de notas emitidas por categoria
                        </div>
                    </div>
                }
                style={{ background: '#FFFFFF', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}
                bodyStyle={{ padding: 16 }}
            >
                {isLoading ? (
                    <div className="flex justify-center items-center" style={{ height: 160 }}>
                        <Spin />
                    </div>
                ) : (
                    <Row gutter={[20, 16]} align="middle">
                        <Col span={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={data}
                                                dataKey="total"
                                                nameKey="type"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={34}
                                                outerRadius={54}
                                                paddingAngle={3}
                                                animationDuration={0}
                                            >
                                                {data.map((entry) => (
                                                    <Cell key={entry.type} fill={TYPE_COLORS[entry.type] ?? DEFAULT_COLOR} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {data.map((entry) => {
                                        const color = TYPE_COLORS[entry.type] ?? DEFAULT_COLOR;
                                        const percentage = total > 0 ? ((entry.total / total) * 100).toFixed(1) : '0';
                                        return (
                                            <div
                                                key={entry.type}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    fontSize: 12,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    paddingBottom: 2,
                                                }}
                                            >
                                                <span style={{ fontWeight: 600, color }}>{entry.type}</span>
                                                <span style={{ fontWeight: 700, color: '#1e293b' }}>
                                                    {poeValorComPontosEmMil(entry.total)}{' '}
                                                    <span style={{ fontWeight: 400, color: '#64748b', fontSize: 11 }}>({percentage}%)</span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                    {total > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, paddingTop: 4 }}>
                                            <span style={{ fontWeight: 600, color: '#64748b' }}>Total</span>
                                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{poeValorComPontosEmMil(total)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
                                    Qualidade por tipo
                                </div>
                                {data.map((item) => {
                                    const percent = item.total > 0 ? (item.totalValid / item.total) * 100 : 0;
                                    return (
                                        <div key={item.type} style={{ marginBottom: 6 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                                <span style={{ fontWeight: 600, color: TYPE_COLORS[item.type] }}>{item.type}</span>
                                                <span style={{ fontWeight: 600, color: '#0f172a' }}>{percent.toFixed(1)}% válidas</span>
                                            </div>
                                            <div style={{ width: '100%', height: 6, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden' }}>
                                                <div
                                                    style={{
                                                        width: `${percent}%`,
                                                        height: '100%',
                                                        background: TYPE_COLORS[item.type],
                                                        borderRadius: 999,
                                                    }}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                                <span style={{ color: '#16a34a' }}>{poeValorComPontosEmMil(item.totalValid)} válidas</span>
                                                <span>•</span>
                                                <span style={{ color: '#dc2626' }}>{poeValorComPontosEmMil(item.totalError)} erros</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                )}
            </Card>
        );
    }

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Notas por tipo</div>
                    <div style={{ fontWeight: 400, fontSize: 16, color: '#888' }}>
                        Quantidade de notas emitidas por categoria
                    </div>
                </div>
            }
            style={{ background: theme === 'light' ? '#FFFFFF' : '#1f1f1f', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none', height: '100%', flex: 1 }}
            bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
            {isLoading ? (
                <div className="flex justify-center items-center" style={{ height: 260 }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            alignItems: "center",
                            gap: isMobile ? 20 : 26,
                        }}
                    >
                        <div
                            style={{
                                flex: `0 0 ${pieSize}px`,
                                width: pieSize,
                                height: pieSize,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data}
                                        dataKey="total"
                                        nameKey="type"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={innerRadius}
                                        outerRadius={outerRadius}
                                        paddingAngle={3}
                                        animationBegin={0}
                                        animationDuration={600}
                                    >
                                        {data.map((entry) => (
                                            <Cell key={entry.type} fill={TYPE_COLORS[entry.type] ?? DEFAULT_COLOR} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value: number) => [poeValorComPontosEmMil(value), 'Total']}
                                        contentStyle={theme === 'dark' ? { backgroundColor: '#1f1f1f', borderColor: '#303030', color: '#e8e8e8' } : undefined}
                                        itemStyle={theme === 'dark' ? { color: '#e8e8e8' } : undefined}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                width: isMobile ? "100%" : undefined,
                            }}
                        >
                            {data.map((entry) => {
                                const color = TYPE_COLORS[entry.type] ?? DEFAULT_COLOR;
                                const percentage = total > 0 ? ((entry.total / total) * 100).toFixed(1) : '0';
                                return (
                                    <div
                                        key={entry.type}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "6px 0",
                                            borderBottom: `1px solid ${theme === 'light' ? '#f0f0f0' : '#303030'}`
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 20, fontWeight: 600, color }}>{entry.type}</div>

                                        </div>
                                        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color }}>{poeValorComPontosEmMil(entry.total)}</div>
                                        <div style={{ fontSize: 18, color: '#888' }}>({percentage}%)</div>
                                    </div>
                                );
                            })}
                            {total > 0 && (
                                <div style={{ paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{
                                        fontSize: isMobile ? 18 : 20,
                                        fontWeight: 600, color: '#888'
                                    }}>Total geral</span>
                                    <span style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: theme === 'light' ? '#333' : '#e8e8e8' }}>{poeValorComPontosEmMil(total)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            // marginTop: 28,
                            paddingTop: 12,
                            borderTop: `1px solid ${theme === "light" ? "#f0f0f0" : "#303030"}`,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 18,
                                fontWeight: 600,
                                marginBottom: 16,
                                color: theme === "light" ? "#333" : "#e8e8e8",
                            }}
                        >
                            Qualidade por tipo
                        </div>

                        {data.map((item, index) => {
                            const percent =
                                item.total > 0
                                    ? (item.totalValid / item.total) * 100
                                    : 0;

                            const valid = item.totalValid;
                            const error = item.totalError;

                            return (
                                <div
                                    key={item.type}
                                    style={{
                                        marginBottom: 8,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: 6,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 16,
                                                color: TYPE_COLORS[item.type],
                                            }}
                                        >
                                            {item.type}
                                        </span>

                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 16,
                                            }}
                                        >
                                            {percent.toFixed(2)}% válidas
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            width: "100%",
                                            height: 8,
                                            borderRadius: 999,
                                            background: theme === "light" ? "#f0f0f0" : "#303030",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${percent}%`,
                                                height: "100%",
                                                background: TYPE_COLORS[item.type],
                                                borderRadius: 999,
                                            }}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "right",
                                            marginTop: 10,
                                            fontSize: 16,
                                        }}
                                    >
                                        <span style={{ color: "#52c41a" }}>
                                            {poeValorComPontosEmMil(valid)} válidas
                                        </span>

                                        <Divider type='vertical' />

                                        <span style={{ color: "#ff4d4f" }}>
                                            {poeValorComPontosEmMil(error)} erros
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </Card>
    );
}
